import { type DocumentDetail } from '@apm/contracts';
import { uploadFile } from '../../lib/upload';
import { documentApi } from './api';

/** Trần tiêu đề của `CreateDocumentInput`. */
const MAX_TITLE = 300;

/**
 * Tiêu đề của tài liệu tạo từ một tệp: tên tệp bỏ đuôi (`Hợp đồng.v2.pdf` ⇒
 * `Hợp đồng.v2`). Tên chỉ có đuôi (`.gitignore`) thì giữ nguyên — bỏ đuôi ra
 * chuỗi rỗng, và API từ chối tiêu đề rỗng.
 */
export function documentTitleOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  const base = (dot > 0 ? fileName.slice(0, dot) : fileName).trim();
  return (base || fileName.trim()).slice(0, MAX_TITLE);
}

export interface UploadDocumentsResult {
  readonly created: readonly DocumentDetail[];
  readonly failed: readonly { readonly name: string; readonly error: unknown }[];
}

/**
 * "Tải lên tài liệu" ở tab Điều lệ — APM-PDOC-b: mỗi tệp thành một tài liệu
 * của dự án, không qua form (người dùng chốt 2026-09-28). Loại để trống ⇒ API
 * ghi `other`; sửa tiêu đề/loại sau ở chi tiết tài liệu.
 *
 * Cùng hai bước với form "Tài liệu mới" của `DocumentsPanel`: tệp lên vùng dự án
 * trước (presign → PUT thẳng S3 → commit, luật #3), rồi mới tạo tài liệu trỏ tới
 * nó — `CreateDocumentInput` cần `fileId` đã `committed`.
 *
 * Tuần tự, không song song: mỗi tệp đã là một lượt PUT lớn thẳng lên S3, và
 * chạy song song thì lỗi hạn mức lưu trữ của tệp thứ ba sẽ nổ giữa lúc tệp thứ
 * nhất còn đang chạy. Tệp nào hỏng thì chỉ tệp đó hỏng — các tệp sau vẫn đi.
 */
export async function uploadDocuments(
  projectId: string,
  files: readonly File[],
): Promise<UploadDocumentsResult> {
  const created: DocumentDetail[] = [];
  const failed: { name: string; error: unknown }[] = [];
  for (const file of files) {
    try {
      const fileId = await uploadFile(file, { projectId, purpose: 'document' });
      created.push(
        await documentApi.create(projectId, { title: documentTitleOf(file.name), fileId }),
      );
    } catch (error) {
      failed.push({ name: file.name, error });
    }
  }
  return { created, failed };
}
